import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import ConnectFormContainer from '../Containers/ConnectFormContainer';
import MainLogo from '../Titles/MainLogo';
import Subtitle from '../Texts/Subtitle';
import SmallText from '../Texts/SmallText';
import { getApiUrl } from '../../config/api';
import { logAuth } from '../../utils/authDebug';
import { redirectAfterAuth } from '../../utils/postAuthRedirect';

const SUCCESS_REDIRECT_DELAY_MS = 1500;
const ERROR_REDIRECT_DELAY_MS = 3000;

type VerifyAccountProps = {
  onAuthenticated?: () => Promise<void>;
};

const VerifyAccount: React.FC<VerifyAccountProps> = ({ onAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const token = new URLSearchParams(location.search).get('token');
  const [status, setStatus] = useState<'checking' | 'ok' | 'error'>('checking');

  const loginPath = (() => {
    const langMatch = location.pathname.match(/^\/([a-z]{2})\b/);
    return `/${langMatch?.[1] || 'fr'}`;
  })();

  useEffect(() => {
    if (!token) {
      navigate(loginPath, { replace: true });
      return;
    }

    let redirectTimer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(getApiUrl('/api/v1/auth/verify'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });
        const data = await res.json().catch(() => ({}));
        logAuth('verify response', {
          ok: res.ok,
          status: res.status,
          success: data?.success,
          hasJwtoken: !!data?.jwtoken,
        });
        if (!res.ok || !data?.success) throw new Error('Verification failed');
        if (cancelled) return;

        setStatus('ok');
        redirectTimer = setTimeout(() => {
          if (!cancelled) {
            void (onAuthenticated ? onAuthenticated() : redirectAfterAuth(navigate));
          }
        }, SUCCESS_REDIRECT_DELAY_MS);
      } catch {
        if (!cancelled) {
          setStatus('error');
          redirectTimer = setTimeout(() => {
            if (!cancelled) {
              navigate(loginPath, { replace: true });
            }
          }, ERROR_REDIRECT_DELAY_MS);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [token, navigate, loginPath, onAuthenticated]);

  return (
    <ConnectFormContainer>
      <MainLogo />
      <Subtitle>{t('auth.verifyTitle')}</Subtitle>
      <Box mt={2}>
        {status === 'checking' ? <SmallText>{t('auth.verifyChecking')}</SmallText> : null}
        {status === 'ok' ? <Alert severity="success">{t('auth.verifySuccess')}</Alert> : null}
        {status === 'error' ? <Alert severity="error">{t('auth.verifyFailed')}</Alert> : null}
      </Box>
      {status === 'ok' ? (
        <Typography variant="body2" sx={{ mt: 2 }}>
          {t('auth.verifyRedirectToApp')}
        </Typography>
      ) : null}
      {status === 'error' ? (
        <Typography variant="body2" sx={{ mt: 2 }}>
          {t('auth.verifyRedirectToLogin')}
        </Typography>
      ) : null}
    </ConnectFormContainer>
  );
};

export default VerifyAccount;
